import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ShieldCheck, CheckCircle2, Loader, Check, Lock, Eye, EyeOff, AlertCircle, CheckCheck } from 'lucide-react';

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Extract token manually since we aren't using a standard Router path
  const token = window.location.pathname.split('/').pop().replace(/\.$/, '');

  // Validation Logic
  const requirements = [
    { label: "8+ Characters", met: newPassword.length >= 8 },
    { label: "One Number", met: /[0-9]/.test(newPassword) },
    { label: "Upper Case", met: /[A-Z]/.test(newPassword) },
    { label: "Special Char", met: /[^A-Za-z0-9]/.test(newPassword) }
  ];

  const allMet = requirements.every(req => req.met);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword !== '';

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (!passwordsMatch || !allMet) return; // Added check for strength

    setLoading(true);
    try {
      const API_BASE = "https://quopro-backend.onrender.com";

      // Use PATCH to match your controller logic
      await axios.patch(`${API_BASE}/api/auth/reset-password/${token}`, {
        password: newPassword
      });

      setResetSuccess(true);
    } catch (err) {
      console.error("Reset Error:", err);
      alert(err.response?.data?.message || "Link invalid. Try requesting a new one.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-md bg-white rounded-[2.5rem] p-10 shadow-2xl border border-slate-100 animate-in zoom-in-95 duration-500">

        {!resetSuccess ? (
          <form onSubmit={handleUpdatePassword} className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-indigo-100/50">
                <ShieldCheck className="text-indigo-600" size={32} />
              </div>
              <h3 className="text-2xl font-black text-slate-900 tracking-tight">Password Reset</h3>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mt-1">
                Update your QuoPro credentials
              </p>
            </div>

            {/* New Password Input */}
            <div className="space-y-1.5">
              <label className="text-[9px] font-black uppercase text-slate-400 ml-4 tracking-widest">New Password</label>
              <div className="relative group">
                <Lock className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-indigo-500 transition-colors" size={18} />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={`w-full pl-14 pr-14 py-4 bg-gray-50 border rounded-[22px] outline-none font-bold transition-all duration-300 ${newPassword ? (allMet ? "border-emerald-500 bg-emerald-50/30" : "border-amber-400 bg-amber-50/30") : "border-gray-100 focus:border-indigo-500"
                    }`}
                  required
                />

                {/* Container for trailing icons */}
                <div className="absolute right-5 top-1/2 -translate-y-1/2 flex items-center gap-2">
                  {allMet && <CheckCircle2 size={20} className="text-emerald-500 animate-in fade-in zoom-in" />}
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-slate-400 hover:text-indigo-500 transition-colors focus:outline-none"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </div>

            {/* Confirm Password Input */}
            <div className="space-y-1.5">
              <label className="text-[9px] font-black uppercase text-slate-400 ml-4 tracking-widest">Confirm Password</label>
              <div className="relative group">
                <CheckCheck className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-indigo-500 transition-colors" size={18} />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full pl-14 pr-14 py-4 bg-gray-50 border rounded-[22px] outline-none font-bold transition-all duration-300 ${confirmPassword ? (passwordsMatch ? "border-emerald-500 bg-emerald-50/30" : "border-red-400 bg-red-50/30") : "border-gray-100 focus:border-indigo-500"
                    }`}
                  required
                />

                {/* Container for trailing icons */}
                <div className="absolute right-5 top-1/2 -translate-y-1/2 flex items-center gap-2">
                  {passwordsMatch && <CheckCircle2 size={20} className="text-emerald-500 animate-in fade-in zoom-in" />}
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="text-slate-400 hover:text-indigo-500 transition-colors focus:outline-none"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </div>

            {/* Security Checklist */}
            {newPassword && (
              <div className="p-5 bg-slate-50 rounded-[1.8rem] border border-slate-100 transition-all duration-500">
                <div className="flex justify-between items-center mb-4 px-1">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Strength Check</p>
                  <span className={`text-[8px] font-black uppercase tracking-widest ${allMet ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {allMet ? 'Verified' : 'Weak'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-y-3 gap-x-4">
                  {requirements.map((req, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center transition-all duration-300 ${req.met ? "bg-emerald-500" : "bg-slate-200"}`}>
                        {req.met ? <Check size={10} className="text-white" strokeWidth={4} /> : <div className="w-1 h-1 bg-white rounded-full" />}
                      </div>
                      <span className={`text-[10px] font-bold transition-colors duration-300 ${req.met ? "text-slate-900" : "text-slate-400"}`}>{req.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Error Message for Mismatch */}
            {confirmPassword && !passwordsMatch && (
              <div className="flex items-center gap-2 px-5 py-3 bg-red-50 border border-red-100 rounded-2xl animate-in slide-in-from-top-2">
                <AlertCircle size={14} className="text-red-500" />
                <span className="text-[10px] font-bold text-red-600">Passwords do not match yet</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !allMet || !passwordsMatch}
              className="w-full bg-indigo-600 text-white py-5 rounded-[22px] font-black text-xs uppercase tracking-[0.2em] hover:bg-indigo-700 transition-all flex items-center justify-center gap-3 shadow-xl shadow-indigo-100 disabled:opacity-30 disabled:grayscale disabled:cursor-not-allowed transform active:scale-[0.98]"
            >
              {loading ? <Loader className="animate-spin" size={18} /> : "Update Password"}
            </button>
          </form>
        ) : (
          /* SUCCESS VIEW */
          <div className="text-center py-6 animate-in fade-in zoom-in duration-700">
            <div className="w-24 h-24 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-8 relative">
              <div className="absolute inset-0 rounded-full bg-emerald-100 animate-ping opacity-20" />
              <CheckCircle2 className="text-emerald-500 relative z-10" size={56} />
            </div>
            <h3 className="text-2xl font-black text-slate-900 mb-2 tracking-tight">Access Restored</h3>
            <p className="text-slate-500 font-bold text-xs mb-10 px-8 leading-relaxed">
              Your new password is active. You can now use your updated credentials to enter the platform.
            </p>
            <button
              onClick={() => window.location.href = '/'}
              className="w-full bg-slate-900 text-white py-5 rounded-[22px] font-black text-xs uppercase tracking-[0.2em] hover:bg-indigo-600 transition-all shadow-xl shadow-slate-200"
            >
              Log In Now
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
